import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Play, Save, Plus } from "lucide-react";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { mh, useMhStream } from "@/lib/mh";
import { errText } from "@/lib/http";
import { newNode, orderNodes, linearize } from "@/lib/wf";
import { useAuth } from "@/context/AuthContext";
import { useDeptMap } from "@/components/mh/hooks";
import { Panel, Eyebrow } from "@/components/mh/bits";
import { Btn } from "@/components/mh/controls";
import { WorkflowPalette } from "@/components/mh/wf/WorkflowPalette";
import { WorkflowCanvas } from "@/components/mh/wf/WorkflowCanvas";
import { NodeInspector, MobileSteps } from "@/components/mh/wf/WorkflowParts";
import { WorkflowLive } from "@/components/mh/wf/WorkflowLive";

const useIsMobile = () => {
  const [m, setM] = useState(() => window.matchMedia("(max-width: 767px)").matches);
  useEffect(() => {
    const q = window.matchMedia("(max-width: 767px)");
    const on = () => setM(q.matches);
    q.addEventListener("change", on);
    return () => q.removeEventListener("change", on);
  }, []);
  return m;
};

const RUN_LABEL = { running: "Running", waiting: "Waiting on the desk", completed: "Completed", rejected: "Rejected", failed: "Failed" };

export default function WorkflowBuilder() {
  const { id } = useParams();
  const { canManage } = useAuth();
  const deps = useDeptMap();
  const mobile = useIsMobile();
  const [wf, setWf] = useState(null);
  const [agents, setAgents] = useState([]);
  const [selected, setSelected] = useState(null);
  const [linking, setLinking] = useState(null);
  const [run, setRun] = useState(null);
  const [sheet, setSheet] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [pre, setPre] = useState(null);
  const loadPre = () => mh.preflight(id).then((r) => setPre(r.data)).catch(() => {});

  useEffect(() => {
    mh.workflow(id).then((r) => setWf(r.data)).catch(() => setWf(false));
    mh.agents().then((r) => setAgents(r.data)).catch(() => {});
    mh.workflowRuns(id).then((r) => setRun(r.data[0] || null)).catch(() => {});
    loadPre();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);
  useMhStream((ev) => { if (ev.type === "workflow.run" && ev.wf_run.workflow_id === id) setRun(ev.wf_run); });

  const agentsBy = useMemo(() => Object.fromEntries(agents.map((a) => [a.slug, a])), [agents]);
  const edit = (fn) => { setWf(fn); setDirty(true); };
  const ordered = useMemo(() => (wf ? orderNodes(wf.nodes, wf.edges) : []), [wf]);

  if (wf === false) return <p className="text-muted-foreground" data-testid="wf-not-found">Workflow not found.</p>;
  if (!wf) return <div className="h-[50vh] grid place-items-center font-mono text-xs text-muted-foreground">Loading workflow…</div>;

  const addStep = (p, x, y) => {
    const n = newNode(p.type, x, y, agentsBy[p.slug]);
    if (mobile) {
      const lin = linearize([...ordered, n]);
      edit((w) => ({ ...w, ...lin }));
    } else {
      edit((w) => ({ ...w, nodes: [...w.nodes, n] }));
    }
    setSelected(n.id);
    setSheet(false);
  };
  const pickFromPalette = (p) => {
    const last = wf.nodes[wf.nodes.length - 1];
    addStep(p, last ? last.x + 250 : 40, last ? last.y : 140);
  };
  const updateNode = (n) => edit((w) => ({ ...w, nodes: w.nodes.map((x) => (x.id === n.id ? n : x)) }));
  const removeNode = (nid) => {
    if (mobile) edit((w) => ({ ...w, ...linearize(ordered.filter((n) => n.id !== nid)) }));
    else edit((w) => ({ ...w, nodes: w.nodes.filter((n) => n.id !== nid), edges: w.edges.filter((e) => e.source !== nid && e.target !== nid) }));
    setSelected(null);
  };
  const moveStep = (i, dir) => {
    const list = [...ordered];
    [list[i], list[i + dir]] = [list[i + dir], list[i]];
    edit((w) => ({ ...w, ...linearize(list) }));
  };
  const save = async () => {
    try {
      const r = await mh.updateWorkflow(id, { name: wf.name, department: wf.department, enabled: wf.enabled, nodes: wf.nodes, edges: wf.edges });
      setWf(r.data);
      setDirty(false);
      loadPre();
      toast.success("Workflow saved");
      return true;
    } catch (e) {
      toast.error(errText(e));
      return false;
    }
  };
  const runNow = async () => {
    if (dirty && !(await save())) return;
    try { setRun((await mh.runWorkflow(id)).data); toast.success("Run started"); } catch (e) { toast.error(errText(e)); }
  };
  const sel = wf.nodes.find((n) => n.id === selected);
  const runStates = run?.states;

  return (
    <div className="max-w-[1600px] mx-auto space-y-4" data-testid="workflow-builder-page">
      <Link to="/workflows" className="inline-flex items-center gap-1.5 font-mono text-xs text-muted-foreground hover:text-foreground" data-testid="back-to-workflows"><ArrowLeft className="w-3.5 h-3.5" /> Workflows</Link>
      <div className="flex flex-col lg:flex-row lg:items-center gap-3">
        <input value={wf.name} disabled={!canManage} onChange={(e) => edit((w) => ({ ...w, name: e.target.value }))} data-testid="wf-name-input"
          className="flex-1 min-w-0 bg-transparent font-display text-3xl sm:text-4xl outline-none border-b border-transparent focus:border-white/20" />
        <div className="flex flex-wrap items-center gap-2">
          <label className="flex items-center gap-2 text-sm text-muted-foreground mr-2"><Switch checked={wf.enabled} disabled={!canManage} onCheckedChange={(v) => edit((w) => ({ ...w, enabled: v }))} data-testid="wf-enabled-switch" />{wf.enabled ? "On" : "Off"}</label>
          {canManage && <Btn onClick={save} testId="wf-save-btn"><Save className="w-4 h-4" /> {dirty ? "Save*" : "Save"}</Btn>}
          {canManage && <Btn variant="primary" onClick={runNow} disabled={!wf.enabled} testId="wf-run-btn"><Play className="w-4 h-4" /> Run</Btn>}
        </div>
      </div>
      {run && (
        <div className="font-mono text-[11px] text-muted-foreground flex flex-wrap gap-3" data-testid="wf-run-status">
          <span style={{ color: run.status === "waiting" ? "#FFB020" : ["rejected", "failed"].includes(run.status) ? "#FF3B5C" : "#D4FF3A" }}>{RUN_LABEL[run.status] || run.status}</span>
          <span data-testid="wf-run-runtime">{run.runtime === "openclaw" ? "on your OpenClaw" : "on the simulation"}</span>
          <span>confidence {Math.round(run.confidence * 100)}%</span>
          {run.error && <span className="text-[#FF3B5C]" data-testid="wf-run-error">{run.error}</span>}
          {run.status === "waiting" && <Link to="/approvals" className="underline" data-testid="wf-open-desk">Open the desk to approve →</Link>}
        </div>
      )}
      <WorkflowLive pre={pre} run={run} />

      {mobile ? (
        <div className="space-y-4">
          <MobileSteps list={ordered} agentsBy={agentsBy} runStates={runStates} onMove={moveStep} onRemove={removeNode} onSelect={setSelected} selected={selected} editable={canManage} />
          {canManage && <Btn onClick={() => setSheet(true)} testId="wf-mobile-add"><Plus className="w-4 h-4" /> Add step</Btn>}
          {sel && <Panel cls="p-4"><NodeInspector node={sel} agents={agents} onChange={updateNode} onDelete={() => removeNode(sel.id)} editable={canManage} /></Panel>}
          <Sheet open={sheet} onOpenChange={setSheet}>
            <SheetContent side="bottom" className="h-[80vh] bg-[#0A0A0F] border-white/10 flex flex-col">
              <SheetHeader><SheetTitle className="font-display text-2xl font-normal">Add a step</SheetTitle></SheetHeader>
              <div className="flex-1 min-h-0 mt-3"><WorkflowPalette agents={agents} deps={deps} onPick={(p) => addStep(p, 0, 0)} /></div>
            </SheetContent>
          </Sheet>
        </div>
      ) : (
        <div className="grid grid-cols-[240px_1fr_280px] gap-4 h-[calc(100vh-260px)] min-h-[520px]">
          <Panel cls="p-3 min-h-0 flex flex-col">{canManage ? <WorkflowPalette agents={agents} deps={deps} onPick={pickFromPalette} /> : <p className="text-sm text-muted-foreground p-2">View-only access.</p>}</Panel>
          <WorkflowCanvas wf={wf} setWf={edit} agentsBy={agentsBy} deps={deps} selected={selected} setSelected={setSelected} linking={linking} setLinking={setLinking}
            runStates={runStates} editable={canManage} onDropStep={addStep} />
          <Panel cls="p-4 overflow-y-auto"><Eyebrow cls="mb-3">Inspector</Eyebrow><NodeInspector node={sel} agents={agents} onChange={updateNode} onDelete={() => removeNode(sel.id)} editable={canManage} /></Panel>
        </div>
      )}
    </div>
  );
}
